import * as React from 'react'

import { MobXProviderContext } from 'mobx-react'

import { LocationType, PermitFieldType } from '~/client/graph/types.generated'
import { withKeepSelected } from '~/client/src/shared/components/ResourcePicker/resourceWorkflowVisibility'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import type LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import { getResourceTypeIdForFieldType } from '~/client/src/shared/utils/permitFieldToResourceType'

import type { SelectableResourceTypeId } from '../../../../WorkflowFieldRestriction/variants/ResourceTypeSelectionContent'

interface IOptions {
  fieldType: PermitFieldType
  restrictedIds: string[]
  restrictedTypes: LocationType[]
  selectedIds: string[]
}

// What a rule value picker lists for its field: the field's resource type, its label,
// and the records it offers.
export function useResourceValueSelection({
  fieldType,
  restrictedIds,
  restrictedTypes,
  selectedIds,
}: IOptions) {
  const { locationAttributesStore } = React.useContext(MobXProviderContext) as {
    locationAttributesStore: LocationAttributesStore
  }
  // A location or equipment pick must be one the rule tag can show, which reads the
  // legacy kind stores. Read in render, where the observer host tracks it.
  const legacyById = locationAttributesStore.hierarchyAllAttributesMap

  const resourceTypeId = (getResourceTypeIdForFieldType(fieldType) ??
    ResourceTypeId.LOCATIONS) as SelectableResourceTypeId
  const isMaterial = resourceTypeId === ResourceTypeId.MATERIALS
  const resourceLabel = isMaterial
    ? Localization.translator.materials
    : resourceTypeId === ResourceTypeId.OFFLOADING_EQUIPMENT
      ? Localization.translator.equipment
      : Localization.translator.locations

  const recordPredicate = React.useMemo(() => {
    const restrictedIdSet = new Set(restrictedIds)
    const restrictedTypeSet = new Set(restrictedTypes)
    return withKeepSelected((record: IResourceRecord) => {
      if (restrictedIdSet.has(record.id)) return false
      if (isMaterial) return true
      const legacy = legacyById[record.id]
      return !!legacy && !restrictedTypeSet.has(legacy.type)
    }, selectedIds)
  }, [restrictedIds, restrictedTypes, isMaterial, legacyById, selectedIds])

  return { resourceTypeId, resourceLabel, recordPredicate }
}
